import { useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { ChevronLeftIcon, ChevronRightIcon } from "./icons";

export type IdeTabItem<T extends string = string> = { id: T; label: string };

export function IdeTabStrip<T extends string>({
  tabs,
  activeId,
  onSelect,
  ariaLabel,
}: {
  tabs: readonly IdeTabItem<T>[];
  activeId: T;
  onSelect: (id: T) => void;
  ariaLabel: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const didMountRef = useRef(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanScrollLeft(scrollLeft > 1);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(update);
      observer.observe(el);
      const inner = el.firstElementChild;
      if (inner) observer.observe(inner);
    }
    return () => {
      el.removeEventListener("scroll", update);
      observer?.disconnect();
    };
  }, [tabs.length]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const active = container.querySelector('[aria-selected="true"]') as HTMLElement | null;
    if (!active) return;
    active.scrollIntoView({
      behavior: didMountRef.current ? "smooth" : "auto",
      inline: "nearest",
      block: "nearest",
    });
    didMountRef.current = true;
  }, [activeId]);

  const scrollByAmount = (delta: number) => {
    containerRef.current?.scrollBy({ left: delta, behavior: "smooth" });
  };

  const needsOverflow = canScrollLeft || canScrollRight;

  return (
    <div className="flex items-stretch border-b border-border bg-card">
      <div className="relative min-w-0 flex-1">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className={cn(
            "absolute inset-y-0 left-0 z-10 h-full w-8 rounded-none bg-linear-to-r from-card from-55% to-transparent text-muted-foreground transition-opacity hover:bg-linear-to-r hover:text-foreground",
            canScrollLeft ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
          )}
          onClick={() => scrollByAmount(-180)}
          aria-label="Scroll tabs left"
          aria-hidden={!canScrollLeft}
          tabIndex={canScrollLeft ? 0 : -1}
        >
          <ChevronLeftIcon />
        </Button>
        <div
          className="overflow-x-auto overflow-y-hidden pb-px [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          ref={containerRef}
        >
          <div
            className="relative flex min-w-full w-max flex-nowrap gap-1 px-7 py-1.5"
            role="tablist"
            aria-label={ariaLabel}
          >
            {tabs.map((tab) => (
              <Button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeId === tab.id}
                variant="ghost"
                size="sm"
                className={cn(
                  "h-7 shrink-0 rounded-md px-3 text-[0.8125rem] font-medium text-muted-foreground shadow-none",
                  activeId === tab.id &&
                    "rounded-b-none bg-background font-semibold text-foreground shadow-[inset_0_-2px_0_0_var(--primary)]"
                )}
                onClick={() => onSelect(tab.id)}
              >
                {tab.label}
              </Button>
            ))}
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className={cn(
            "absolute inset-y-0 right-0 z-10 h-full w-8 rounded-none bg-linear-to-l from-card from-55% to-transparent text-muted-foreground transition-opacity hover:bg-linear-to-l hover:text-foreground",
            canScrollRight ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
          )}
          onClick={() => scrollByAmount(180)}
          aria-label="Scroll tabs right"
          aria-hidden={!canScrollRight}
          tabIndex={canScrollRight ? 0 : -1}
        >
          <ChevronRightIcon />
        </Button>
      </div>
      {needsOverflow ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="h-full w-8 shrink-0 rounded-none border-l border-border text-base font-bold text-muted-foreground"
              title="All tabs"
              aria-label="Open tab list"
            >
              ⋯
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-64 min-w-44 overflow-y-auto">
            {tabs.map((tab) => (
              <DropdownMenuItem
                key={tab.id}
                className={cn(activeId === tab.id && "bg-primary/10 font-semibold")}
                onSelect={() => onSelect(tab.id)}
              >
                {tab.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}
